/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../logging/Log";

const TAG: string = "EasDismissPolicy";

/**
 * Display modes whose Emergency Alert System (EAS) OSD template carries a
 * "Press BACK to dismiss" footer.
 *
 * Today only the United States template offers one. The set is the generic
 * mechanism rather than a special case for the US: adding a market that
 * permits manual dismissal is one entry here and nothing else, and a market
 * left out of it can never be dismissed however its alerts are flagged.
 *
 * Matched case-insensitively against `easConfiguration.easDisplayMode`, the
 * same value `EasOsdTemplateSelector` resolves placement from.
 */
const DISMISSIBLE_DISPLAY_MODES: ReadonlySet<string> = new Set<string>(["us"]);

/**
 * Whether this market's OSD template shows a dismiss footer at all.
 *
 * @param displayMode - `easConfiguration.easDisplayMode` (e.g. "us", "canada")
 * @returns True when the template can offer manual dismissal
 */
export function supportsDismissFooter(displayMode: string): boolean {
  return DISMISSIBLE_DISPLAY_MODES.has(displayMode.trim().toLowerCase());
}

/**
 * Whether an alert may actually be dismissed by the viewer.
 *
 * ## The footer is the contract
 *
 * An alert is dismissible only when its market's template shows the footer
 * *and* the alert itself permits exit. Both halves are required, and the
 * market's half comes first: if the template shows no footer then nothing on
 * screen tells the viewer a way out exists, so there must not be one. An
 * alert that could be dismissed by a key the OSD never mentions is a worse
 * failure than one that cannot be dismissed at all — the viewer cannot
 * discover it, and a stray BACK would clear an emergency they had not read.
 *
 * Canada is the case this exists for. Its full-screen template carries no
 * footer by product decision, so a Canadian alert is never dismissible —
 * including the `allowUserExit: true` that is not expected to arrive, and
 * would be honoured by the wire field alone.
 *
 * This one answer drives both halves of the behaviour: `EasInputGate` gates
 * the dismiss key on it, and it reaches `EasOsd` as the footer's own
 * condition. They cannot disagree, because there is nothing to disagree with.
 *
 * @param displayMode - `easConfiguration.easDisplayMode` (e.g. "us", "canada")
 * @param allowUserExit - The alert's own `EasAlert.allowUserExit`
 * @returns True when the viewer may dismiss this alert
 */
export function resolveIsDismissible(
  displayMode: string,
  allowUserExit: boolean
): boolean {
  if (!supportsDismissFooter(displayMode)) {
    if (allowUserExit) {
      Log.i(
        TAG,
        `resolveIsDismissible(): alert permits exit but the "${displayMode}" ` +
          "template shows no dismiss footer — holding it non-dismissible"
      );
    }
    return false;
  }
  return allowUserExit;
}
